import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchPorAsins } from "../porAsinList/porAsinListSlice";
import { RootState } from "../app/rootReducer";
import {
  Table,
  CollectionPreferences,
  Pagination,
  Header,
  Popover,
  StatusIndicator,
  Spinner,
  SpaceBetween,
  Link,
} from "@amzn/awsui-components-react/polaris";
import { fetchEditableAttributes } from "../porAsinList/porAsinListSlice";
import { SortFacet } from "../../from-server/asin-central/search";
import {
  baselinePageSelectorOptions,
  baselineTableColumnDefinitions,
  DATE_BASELINE_COLUMN_DEFINITIONS,
  EDITABLE_DATE_BASELINE_COLUMN_DEFINITIONS,
} from "./baselineTable-config";
import {
  setPreferences,
  mergeVisibleContentPreferences,
  setSort,
  setBaselinePageNumber,
  setViewingBaselineTable,
  setEditingBaselineTable,
  fetchGetCandidateDates,
  fetchRecordAsinMarketplaceDates,
  fetchBaselineRequest,
} from "./baselineTableConnector";
import {
  defaultSort,
  TableHeaderMessage,
  EmptyTableState,
} from "../porAsinList/TableCommonElements";
import { writeToLocalStorage } from "../../util/localStorageUtils";
import { Button } from "@amzn/awsui-components-react";
import { trackButtonClick, trackComponentView } from "../telemetry/telemetry";
import {
  FullAsinWithAudits,
  RestrictedAsin,
} from "../../from-server/asin-central/model";
import {
  clearNotifications,
  createNotificationDefinition,
} from "../notification/notificationConnector";
import {
  clearSelectedAnnounceDates,
  clearSelectedOnlineStreetDates,
  SelectedAsinMarketplaceDate,
  startGetCandidateDates,
} from "./candidateDatesConnector";
import { AsinMarketplace } from "@amzn/coral_com-amazon-asincentral-common/dist/umd/AsinMarketplace";
import { AsinMarketplaceDate } from "@amzn/coral_com-amazon-deviceslaunchcontrolservice/dist/umd/AsinMarketplaceDate";
import { AsinMarketplaceDates } from "@amzn/coral_com-amazon-deviceslaunchcontrolservice/dist/umd/AsinMarketplaceDates";
import {
  LaunchControlDate,
  LaunchControlDateDates,
} from "@amzn/coral_com-amazon-deviceslaunchcontrolservice";
import { AuthorizedAttribute } from "../../from-server/brass/model";
import {
  setBaselineAttributeQuery,
  setQueryString,
} from "../searchSideBar/searchSideBarConnector";
import { MilestoneScheduleTable } from "./milestoneScheduleTable";
import { currentPagePorAsinsBelongToSameProgram } from "../common/PorAsinListUtils";

const BaselineActionButtons = () => {
  return (
    <>
      <BaselineActionButton />
    </>
  );
};

export const BaselineEditActionButtons = () => {
  const dispatch = useDispatch();
  const {
    selectedAnnounceDates,
    selectedOnlineStreetDates,
    programId,
    scheduleId,
    isGettingCandidates,
  } = useSelector((state: RootState) => state.candidateDates);
  const { existingBaselineRequest } = useSelector(
    (state: RootState) => state.asinBaselineTableControl
  );

  useEffect(() => {
    trackComponentView("baselineTable", "table");
  }, []);

  return (
    <div data-testid="baseline-edit-actions">
      {isGettingCandidates && <Spinner size="big" />}
      <Button
        data-testid="baseline-cancel-button"
        children="Cancel"
        disabled={isGettingCandidates}
        onClick={() => {
          trackButtonClick("baselineTable", "cancel", {});
          dispatch(setViewingBaselineTable());
          dispatch(clearSelectedAnnounceDates());
          dispatch(clearSelectedOnlineStreetDates());
        }}
      />
      &nbsp;&nbsp;&nbsp;
      <Button
        data-testid="baseline-save-button"
        children="Save"
        variant="primary"
        disabled={isGettingCandidates}
        onClick={() => {
          trackButtonClick("baselineTable", "save", {
            announceDateCount: selectedAnnounceDates.length,
            onlineStreetDateCount: selectedOnlineStreetDates.length,
          });
          const datesToRecord: AsinMarketplaceDates = new AsinMarketplaceDates(
            Object.keys(selectedAnnounceDates).map((asinMp) => {
              // TODO: Eventually refactor if we want to allow baselining single date types, asssuming that announce and street date are always both selected
              const selectedAnnounceDate: SelectedAsinMarketplaceDate =
                selectedAnnounceDates[asinMp];
              const selectedOnlineStreetDate: SelectedAsinMarketplaceDate =
                selectedOnlineStreetDates[asinMp];
              const combinedDates = selectedAnnounceDate.dates.concat(
                selectedOnlineStreetDate.dates
              );

              return AsinMarketplaceDate.clone({
                asin: selectedAnnounceDate.asin,
                obfuscatedMarketplaceId:
                  selectedAnnounceDate.obfuscatedMarketplaceId,
                dates: new LaunchControlDateDates(
                  combinedDates.map((date) =>
                    LaunchControlDate.clone({
                      dateType: date.dateType,
                      dateValue: date.dateValueInMillis,
                      status: date.dateStatus,
                    })
                  )
                ),
              });
            })
          );
          const dateTypes = datesToRecord.flatMap((d) =>
            d.dates.map((x) => x.dateType)
          );
          dispatch(
            fetchRecordAsinMarketplaceDates(
              datesToRecord,
              [...new Set(dateTypes)],
              scheduleId,
              programId,
              existingBaselineRequest
            )
          );
          dispatch(
            createNotificationDefinition(
              "success",
              "Baseline Request Sent",
              true,
              `Request to baseline ${datesToRecord.length} dates sent.`
            )
          );
          dispatch(clearSelectedAnnounceDates());
          dispatch(clearSelectedOnlineStreetDates());
          dispatch(setViewingBaselineTable());
        }}
      />
    </div>
  );
};

function isButtonDisabled(
  asins: (FullAsinWithAudits | RestrictedAsin)[],
  isLoading: boolean
) {
  return (
    asins.every((item) => item.isRestricted) || asins.length < 1 || isLoading
  );
}

const BaselineActionButton = () => {
  const dispatch = useDispatch();
  const { currentPagePorAsins, isLoading, editableAttributes } = useSelector(
    (state: RootState) => state.porAsinList
  );

  function userIsAuthorized(editableAttributes: AuthorizedAttribute[]) {
    return editableAttributes.some(
      (attribute) =>
        attribute.attributeName === "date_baseline" && attribute.authorized
    );
  }

  function getPopoverContent() {
    if (!userIsAuthorized(editableAttributes)) {
      return (
        <span>
          You must be a member of the{" "}
          <a
            href="https://permissions.amazon.com/a/team/ASIN-Central-Date-Baseline"
            target="_blank"
            rel="noreferrer"
          >
            "ASIN-Central-Date-Baseline"
          </a>{" "}
          team to perform date baselining
        </span>
      );
    }
    if (!currentPagePorAsinsBelongToSameProgram(currentPagePorAsins)) {
      return (
        <span>
          There are restricted ASINs or ASINs from different programs in the
          current page. Please refine your search criteria and try again
        </span>
      );
    }
  }

  return (
    <div data-testid="baseline-action-button-wrapper">
      {(!userIsAuthorized(editableAttributes) ||
        !currentPagePorAsinsBelongToSameProgram(currentPagePorAsins)) && (
        <Popover
          dismissButton={true}
          header=""
          position="top"
          size="medium"
          triggerType="custom"
          content={getPopoverContent()}
        >
          <StatusIndicator type="info" />
        </Popover>
      )}
      <Button
        data-testid="baseline-button"
        variant="primary"
        children="Baseline"
        disabled={
          isButtonDisabled(currentPagePorAsins, isLoading) ||
          !currentPagePorAsinsBelongToSameProgram(currentPagePorAsins) ||
          !userIsAuthorized(editableAttributes)
        }
        onClick={() => {
          trackButtonClick("baselineTable", "baseline", {});
          // TODO: Clear candidate data
          dispatch(
            createNotificationDefinition(
              "info",
              "Getting Candidate Dates",
              true,
              "Getting candidate dates..."
            )
          );
          dispatch(startGetCandidateDates());

          function getCandidateDates(
            asins: (FullAsinWithAudits | RestrictedAsin)[]
          ) {
            // Remove restricted ASINs from list
            const listOfAsinMps = asins
              .filter((x) => !x.isRestricted)
              .map((x) => x as FullAsinWithAudits);

            dispatch(
              fetchGetCandidateDates(
                listOfAsinMps.map(
                  (asinMp) =>
                    new AsinMarketplace(
                      asinMp.asin,
                      asinMp.obfuscatedMarketplaceId
                    )
                )
              )
            );
          }

          getCandidateDates(currentPagePorAsins);
          dispatch(clearNotifications());
          dispatch(
            setEditingBaselineTable({
              currentPagePorAsins,
            })
          );
        }}
      />
    </div>
  );
};

let defaultSet = false;
export const BaselineTable = () => {
  const dispatch = useDispatch();
  const { currentPagePorAsins, isLoading, count, isGettingEditableAttributes } =
    useSelector((state: RootState) => state.porAsinList);
  const {
    isEditing,
    editableBaselineAsins,
    preferences,
    sort,
    descending,
    pageNumber,
    existingBaselineRequest,
  } = useSelector((state: RootState) => state.asinBaselineTableControl);
  const {
    queryString,
    activePropertyFilters,
    activePropertyFiltersVisibilityEnabled,
  } = useSelector((state: RootState) => state.generateQuery);

  const queryParameters = useSelector(
    (state: RootState) => state.generateQuery.queryParameters
  );
  const isBaselineQuery = useSelector(
    (state: RootState) => state.generateQuery.isBaselineQuery
  );

  useEffect(() => {
    const baselineId = queryParameters["baselineId"]?.value as
      | string
      | undefined;
    if (baselineId && !existingBaselineRequest) {
      dispatch(fetchBaselineRequest(baselineId));
    }
  }, [queryParameters]);

  const getSort = (): SortFacet[] =>
    sort === undefined
      ? defaultSort
      : //TODO: Uncomment this when the backend is able to sort on this attribute.
        // [
        //     {
        //       attributeName: "baselineStatus",
        //       ascending: true,
        //     },
        //   ]
        [
          {
            attributeName: sort?.sortingField,
            ascending: !descending,
          },
        ];

  function setBaselineDefaultSearch() {
    if (defaultSet) {
      // If the user changes the criteria, we don't want to override them.
      return;
    }
    defaultSet = true;
    dispatch(
      setBaselineAttributeQuery({
        attribute: "baselineStatus",
        queryState: {
          value: "ANNOUNCE_DATE_NON_COMPLIANT,ONLINE_STREET_DATE_NON_COMPLIANT",
        },
      })
    );

    dispatch(setQueryString());
  }

  useEffect(() => {
    if (queryString) {
      const offset = preferences.pageSize * (pageNumber - 1);
      // By default, we want to search for non-compliant dates.
      setBaselineDefaultSearch();
      if (isBaselineQuery) {
        dispatch(
          fetchPorAsins(offset, preferences.pageSize, queryString, getSort())
        );
      }
    }
  }, [
    pageNumber,
    queryString,
    preferences.pageSize,
    dispatch,
    sort,
    descending,
  ]);

  useEffect(() => {
    if (activePropertyFiltersVisibilityEnabled) {
      dispatch(
        mergeVisibleContentPreferences({
          activeColumns: Object.keys(activePropertyFilters ?? {}),
        })
      );
    }
  }, [queryString, activePropertyFiltersVisibilityEnabled]);

  useEffect(() => {
    if (!isGettingEditableAttributes) {
      dispatch(fetchEditableAttributes());
    }
  });

  return (
    <SpaceBetween size="l" data-testid="baseline-table-wrapper">
      <MilestoneScheduleTable />
      <Table
        data-testid="baseline-table"
        header={
          <Header
            data-testid="baseline-header"
            description={TableHeaderMessage(
              (pageNumber - 1) * preferences.pageSize,
              currentPagePorAsins.length,
              count
            )}
            actions={
              isEditing ? (
                <BaselineEditActionButtons />
              ) : (
                <BaselineActionButtons />
              )
            }
            info={
              <Link
                variant="info"
                target="_blank"
                rel="noreferrer"
                href="https://w.amazon.com/bin/view/PSLM/Working_with_us/ASIN_Central#HASINDateBaselineCentral"
              >
                Info
              </Link>
            }
          >
            Date Baseline
          </Header>
        }
        loading={isLoading}
        loadingText={"Loading"}
        empty={EmptyTableState(
          "No ASINs Currently Need Baseline",
          'Someone might have baselined these ASINs already. To see more ASINs, remove the "Baseline Status" or "Baseline Id" filter or change the search criteria'
        )}
        columnDefinitions={
          isEditing
            ? EDITABLE_DATE_BASELINE_COLUMN_DEFINITIONS
            : DATE_BASELINE_COLUMN_DEFINITIONS
        }
        items={isEditing ? editableBaselineAsins : currentPagePorAsins}
        stickyHeader={true}
        sortingColumn={sort}
        sortingDescending={descending}
        onSortingChange={(event) => dispatch(setSort(event.detail))}
        pagination={
          isEditing ? undefined : (
            <Pagination
              data-testid="baseline-pagination"
              pagesCount={Math.ceil(count / preferences.pageSize)}
              currentPageIndex={pageNumber}
              disabled={isLoading}
              onChange={({ detail }) => {
                dispatch(
                  setBaselinePageNumber({
                    pageNumber: detail.currentPageIndex,
                  })
                );
              }}
            />
          )
        }
        visibleColumns={preferences.visibleContent}
        preferences={
          isEditing ? undefined : (
            <CollectionPreferences
              data-testid="baseline-preferences"
              onConfirm={(event) => {
                writeToLocalStorage(
                  "BASELINE_TABLE_PREFERENCES",
                  JSON.stringify(event.detail)
                );
                dispatch(setPreferences(event.detail));
              }}
              title={"Preferences"}
              confirmLabel={"Confirm"}
              cancelLabel={"Cancel"}
              disabled={isLoading}
              preferences={preferences}
              pageSizePreference={baselinePageSelectorOptions}
              visibleContentPreference={baselineTableColumnDefinitions}
            />
          )
        }
        className={"porTable"}
      />
    </SpaceBetween>
  );
};
